import IMutation from '../Mutations/IMutation';
import MutationType from '../Mutations/MutationType';
import ObjectType from '../Mutations/ObjectType';
import INumeric from '../Models/INumeric';
import { IMutator } from '../Mutations/IMutator';
import IMutatorContext from '../Mutations/IMutatorContext';
import IMutationPatch from '../Mutations/IMutationPatch';
import diffPatcher from '../Mutations/Patcher';
import { EventNames } from '../EventNames';
import INumericCounter from '../Models/INumericCounter';
import { Ref } from 'vue';
import NumericCounterService from '@/core/Services/NumericCounterService';

export class NumericMutator implements IMutator {
    private _matrix = {
        [ObjectType.Numeric]: {
            [MutationType.Created]: this.createNumeric,
            [MutationType.Updated]: this.updateNumeric,
            [MutationType.Deleted]: this.deleteNumeric,
            [MutationType.Patched]: this.patchNumeric,
        },
        [ObjectType.NumericCounter]: {
            [MutationType.Created]: this.createNumericCounter,
            [MutationType.Updated]: this.updateNumericCounter,
            [MutationType.Deleted]: this.deleteNumericCounter,
            [MutationType.Patched]: this.patchNumericCounter,
        },
    };

    async mutate(context: IMutatorContext, mutations: IMutation[], eventName: string): Promise<void> {
        if (eventName !== EventNames.DbObjectsMutated) {
            return;
        }

        const typedContext = context as NumericMutatorContext;
        const changedNumerics = new Map<number, INumeric>();
        for (const mutation of mutations) {
            const objectType = mutation.objectType as keyof typeof this._matrix;

            const mutate = this._matrix[objectType]?.[mutation.type]?.bind(this) as
                | ((context: NumericMutatorContext, objectState: unknown) => Promise<INumeric | INumeric[] | undefined>)
                | undefined;

            if (!mutate) {
                continue;
            }

            const changed = await mutate(typedContext, mutation.objectState);

            if (Array.isArray(changed)) {
                changed.forEach((changedNumeric) => changedNumerics.set(changedNumeric.id, changedNumeric));
            } else if (changed) {
                changedNumerics.set(changed.id, changed);
            }
        }

        if (!typedContext.options.excludeNumeric) {
            return;
        }

        for (const changedNumeric of changedNumerics) {
            if (typedContext.options.excludeNumeric(changedNumeric[1])) {
                const index = typedContext.numerics.value.indexOf(changedNumeric[1]);
                typedContext.numerics.value.splice(index, 1);
            }
        }
    }

    protected async createNumeric(
        context: NumericMutatorContext,
        numeric: INumeric,
    ): Promise<INumeric | INumeric[] | undefined> {
        const ignore = context.options.ignoreNumericCreating ? context.options.ignoreNumericCreating(numeric) : false;

        if (ignore) {
            return;
        }

        return this.updateNumeric(context, numeric);
    }

    protected async updateNumeric(
        context: NumericMutatorContext,
        numeric: INumeric,
    ): Promise<INumeric | INumeric[] | undefined> {
        const carry = context.numerics.value.find((item) => item.id === numeric.id);

        if (context.options.mapCounters && numeric.counterId && numeric.counterId !== carry?.counterId) {
            const result = await NumericCounterService.queryAsync({
                whereId: [numeric.counterId],
            });
            numeric.counter = result[0];
        }

        if (
            context.options.mapCounters &&
            numeric.counterDivisorId &&
            numeric.counterDivisorId !== carry?.counterDivisorId
        ) {
            const result = await NumericCounterService.queryAsync({
                whereId: [numeric.counterDivisorId],
            });
            numeric.counterDivisor = result[0];
        }

        if (carry) {
            Object.assign(carry, numeric);
            return carry;
        }

        context.numerics.value.push(numeric);
        return numeric;
    }

    protected async patchNumeric(
        context: NumericMutatorContext,
        patch: IMutationPatch,
    ): Promise<INumeric | INumeric[] | undefined> {
        if (!patch.id) {
            return;
        }

        const carry = context.numerics.value.find((item) => item.id === patch.id);
        const typedPatch = patch.patch as { counterId?: number[]; counterDivisorId?: number[] };

        if (carry && context.options.mapCounters && typedPatch.counterId && typedPatch.counterId.length > 1) {
            const result = await NumericCounterService.queryAsync({
                whereId: [typedPatch.counterId[1]],
                perPage: 1,
            });
            carry.counter = result[0];
        }

        if (
            carry &&
            context.options.mapCounters &&
            typedPatch.counterDivisorId &&
            typedPatch.counterDivisorId.length > 1
        ) {
            const divisorId = typedPatch.counterDivisorId[1];

            if (divisorId === null) {
                carry.counterDivisor = undefined;
                return;
            }

            if (!divisorId) {
                return;
            }

            const [counterDivisor] = await NumericCounterService.queryAsync({
                whereId: [divisorId],
                perPage: 1,
            });

            carry.counterDivisor = counterDivisor;
        }

        // Fetch the Numeric if it requires.
        if (!carry) {
            const fetchedNumeric = await context.options.fetchNumeric?.(
                diffPatcher.patch({ id: patch.id }, patch.patch) as Partial<INumeric>,
            );

            if (!fetchedNumeric) {
                return;
            }

            if (carry) {
                Object.assign(carry, fetchedNumeric);
                return carry;
            } else {
                context.numerics.value.push(fetchedNumeric);
                return fetchedNumeric;
            }
        }

        diffPatcher.patch(carry, patch.patch);

        return carry;
    }

    protected deleteNumeric(
        context: NumericMutatorContext,
        numeric: INumeric,
    ): Promise<INumeric | INumeric[] | undefined> {
        const index = context.numerics.value.findIndex((item) => item.id === numeric.id);

        if (index !== -1) {
            context.numerics.value.splice(index, 1);
        }

        return Promise.resolve(undefined);
    }

    protected async createNumericCounter(): Promise<INumeric | INumeric[] | undefined> {
        return undefined;
    }

    protected async updateNumericCounter(
        context: NumericMutatorContext,
        numericCounter: INumericCounter,
    ): Promise<INumeric | INumeric[] | undefined> {
        const numerics = context.numerics.value.filter(
            (numeric) => numeric.counterId === numericCounter.id || numeric.counterDivisorId === numericCounter.id,
        );

        numerics.forEach((numeric) => {
            if (numeric.counterId === numericCounter.id) {
                numeric.counter = numeric.counter ? Object.assign(numeric.counter, numericCounter) : numericCounter;
            }

            if (numeric.counterDivisorId === numericCounter.id) {
                numeric.counterDivisor = numeric.counterDivisor
                    ? Object.assign(numeric.counterDivisor, numericCounter)
                    : numericCounter;
            }
        });

        return numerics;
    }

    protected async deleteNumericCounter(): Promise<INumeric | INumeric[] | undefined> {
        return undefined;
    }

    protected async patchNumericCounter(
        context: NumericMutatorContext,
        patch: IMutationPatch,
    ): Promise<INumeric | INumeric[] | undefined> {
        const numerics = context.numerics.value.filter(
            (numeric) => numeric.counterId === patch.id || numeric.counterDivisorId === patch.id,
        );

        numerics.forEach((numeric) => {
            if (numeric.counter?.id === patch.id) {
                diffPatcher.patch(numeric.counter, patch.patch);
            }

            if (numeric.counterDivisor?.id === patch.id) {
                diffPatcher.patch(numeric.counterDivisor, patch.patch);
            }
        });

        return numerics;
    }
}

const defaultMutator = new NumericMutator();

export class NumericMutatorContext implements IMutatorContext {
    public numerics: Ref<INumeric[]>;
    public options: INumericsMutationContextOptions;
    public mutator: IMutator;

    constructor(
        numerics: Ref<INumeric[]>,
        options: INumericsMutationContextOptions | null = null,
        mutator: IMutator = defaultMutator,
    ) {
        this.numerics = numerics;
        this.options = {
            mapCounters: false,
            ...options,
        };
        this.mutator = mutator;
    }
}

export interface INumericsMutationContextOptions {
    mapCounters?: boolean;
    fetchNumeric?: (numeric: Partial<INumeric>) => Promise<INumeric | undefined>;
    excludeNumeric?: (numeric: INumeric) => boolean;
    ignoreNumericCreating?: (numeric: INumeric) => boolean;
}

export default defaultMutator;
